import { useCallback } from 'react';
import PoiActionDetailedLocation from '@alltrails/amplitude/enums/PoiActionDetailedLocation';
import useExploreServiceFilterCounts from '@alltrails/explore-service/hooks/useExploreServiceFilterCounts';
import ActivePOIRoutingOptions from '@alltrails/maps/components/POI/ActivePOIPopupOptions/ActivePOIRoutingOptions';
import POIResults from '@alltrails/maps/components/POI/POIResults/POIResults';
import SelectedPOI from '@alltrails/maps/components/POI/SelectedPOI/SelectedPOI';
import useMap from '@alltrails/maps/hooks/useMap';
import { updateActivePOI, updateShowPOIPopupMenu } from '@alltrails/maps/redux/reducer';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import { useHasCustomRouteEditPermission } from '@/hooks/useHasCustomRouteEditPermission';
import { useLogExploreCardViewed } from '@/utils/analyticsHelpers';
import { useLogPOISelected } from '@/utils/poiAnalyticsHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import FocusedPOIMapComponents from './FocusedPOIMapComponents';

type POIMapComponentsProps = {
  onClick?: (poi: POISearchResult) => void;
  poiLocation: PoiActionDetailedLocation;
  /** For a surface that owns POI visibility itself; overrides hiding POIs behind applied filters. */
  showPOIs?: boolean;
};

const POIMapComponents = ({ onClick, poiLocation, showPOIs }: POIMapComponentsProps) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const logPOISelected = useLogPOISelected();
  const logPOICardViewed = useLogExploreCardViewed('poi');
  const canAddToRoute = useHasCustomRouteEditPermission();
  const isEditing = useAppSelector(state => state.customRoute.isEditing);
  const { adminFilterCount, filterCount } = useExploreServiceFilterCounts();

  // Hide POIs when any filters are applied
  const hasFilters = (filterCount ?? 0) > 0 || (adminFilterCount ?? 0) > 0;
  const shouldShowPOIs = showPOIs ?? !hasFilters;

  const onPOIClick = useCallback(
    (poi: POISearchResult) => {
      logPOISelected(poi, poiLocation);
      onClick?.(poi);
      // Pin clicks set the active POI in POIResults, but hover card clicks only reach this handler
      dispatch(updateActivePOI(poi));
      dispatch(updateShowPOIPopupMenu(true));
    },
    [onClick, logPOISelected, poiLocation, dispatch]
  );

  if (!map) {
    return null;
  }

  return (
    <>
      {shouldShowPOIs && <POIResults onPOICardViewed={logPOICardViewed} onPOIClick={onPOIClick} />}
      <ActivePOIRoutingOptions canAddToRoute={isEditing && canAddToRoute} />
      <SelectedPOI />
      <FocusedPOIMapComponents />
    </>
  );
};

export default POIMapComponents;
